import { useState } from "react";
import { Share2, Plus, Trash2, ChevronDown, ChevronRight, ShieldCheck, Info } from "lucide-react";

const PLATFORMS = [
  { slug: "instagram", label: "Instagram" },
  { slug: "facebook", label: "Facebook" },
  { slug: "tiktok", label: "TikTok" },
  { slug: "youtube", label: "YouTube" },
  { slug: "linkedin", label: "LinkedIn" },
  { slug: "x", label: "X (Twitter)" },
  { slug: "pinterest", label: "Pinterest" },
  { slug: "threads", label: "Threads" },
  { slug: "other", label: "Other" },
];

const PRESENCE = [
  { v: "yes_known", label: "Yes — I know the account links" },
  { v: "yes_help", label: "Yes — help me find them" },
  { v: "no", label: "No social media accounts" },
  { v: "unsure", label: "I'm not sure" },
];

const VERIFY = [
  { v: "confirmed", label: "Confirmed official" },
  { v: "suspected", label: "Suspected official" },
  { v: "unverified", label: "Unverified / possibly unrelated" },
];

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400";
const sel = input + " bg-white";

const num = (v) => (v === "" || v == null ? undefined : Number(v));
const tri = (v) => (v === "true" ? true : v === "false" ? false : undefined);

function emptyAccount() {
  return { platform: "instagram", custom_platform_name: "", url: "", handle: "",
           verification_status: "confirmed", _open: false, m: {} };
}

// Builds the API-shaped payload from internal state (numbers/bools/omit-empty).
function clean(presence, rows) {
  const accounts = rows
    .filter((r) => (r.handle || "").trim() || (r.url || "").trim())
    .map((r) => {
      const m = r.m || {};
      const metrics = {
        followers: num(m.followers), posts_last_30d: num(m.posts_last_30d),
        avg_engagement_rate: num(m.avg_engagement_rate), local_audience_pct: num(m.local_audience_pct),
        profile_visits_30d: num(m.profile_visits_30d), link_clicks_30d: num(m.link_clicks_30d),
        leads_30d: num(m.leads_30d), bookings_30d: num(m.bookings_30d),
        link_in_bio: tri(m.link_in_bio), booking_or_order_link: tri(m.booking_or_order_link),
        profile_complete: tri(m.profile_complete), responds_to_comments: tri(m.responds_to_comments),
      };
      Object.keys(metrics).forEach((k) => metrics[k] === undefined && delete metrics[k]);
      return {
        platform: r.platform,
        custom_platform_name: r.platform === "other" ? (r.custom_platform_name || "") : null,
        url: (r.url || "").trim() || null,
        handle: (r.handle || "").trim() || null,
        verification_status: r.verification_status || "unverified",
        metrics: Object.keys(metrics).length ? metrics : null,
      };
    });
  return { presence, accounts };
}

export function SocialAccountsInput({ onChange }) {
  const [presence, setPresence] = useState("unsure");
  const [rows, setRows] = useState([emptyAccount()]);

  const emit = (p, r) => onChange && onChange(clean(p, r));

  const setP = (v) => {
    setPresence(v);
    const r = (v === "yes_known" || v === "yes_help") && rows.length === 0 ? [emptyAccount()] : rows;
    setRows(r);
    emit(v, r);
  };
  const update = (i, patch) => {
    const r = rows.map((row, idx) => (idx === i ? { ...row, ...patch } : row));
    setRows(r); emit(presence, r);
  };
  const updateM = (i, key, val) => update(i, { m: { ...(rows[i].m || {}), [key]: val } });
  const add = () => { const r = [...rows, emptyAccount()]; setRows(r); emit(presence, r); };
  const remove = (i) => { const r = rows.filter((_, idx) => idx !== i); setRows(r); emit(presence, r); };

  const showAccounts = presence === "yes_known" || presence === "yes_help";

  const MetricNum = ({ i, k, label, ph }) => (
    <label className="block">
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <input type="number" min="0" className={input} placeholder={ph}
        value={rows[i].m?.[k] ?? ""} onChange={(e) => updateM(i, k, e.target.value)}
        data-testid={`social-metric-${k}-${i}`} />
    </label>
  );
  const MetricTri = ({ i, k, label }) => (
    <label className="block">
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <select className={sel} value={rows[i].m?.[k] ?? ""} onChange={(e) => updateM(i, k, e.target.value)}
        data-testid={`social-tri-${k}-${i}`}>
        <option value="">—</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </label>
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="social-input">
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="grid place-items-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600">
          <Share2 className="w-4 h-4" />
        </span>
        <h3 className="font-heading font-bold text-slate-900">Social media</h3>
      </div>
      <p className="text-sm text-slate-600 mb-4">Does this business have social media accounts? This is optional — your scan runs either way.</p>

      <div className="grid sm:grid-cols-2 gap-2 mb-2">
        {PRESENCE.map((o) => (
          <button key={o.v} type="button" onClick={() => setP(o.v)} data-testid={`social-presence-${o.v}`}
            className={`text-left rounded-xl border px-3.5 py-2.5 text-sm transition-colors ${presence === o.v ? "border-indigo-500 bg-indigo-50/60 text-slate-900 font-medium" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
            {o.label}
          </button>
        ))}
      </div>

      {showAccounts && (
        <div className="mt-4 space-y-4" data-testid="social-accounts-list">
          {rows.map((row, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`social-account-${i}`}>
              <div className="grid sm:grid-cols-12 gap-2.5">
                <div className="sm:col-span-3">
                  <span className="text-[11px] font-medium text-slate-500">Platform</span>
                  <select className={sel} value={row.platform} onChange={(e) => update(i, { platform: e.target.value })}
                    data-testid={`social-platform-${i}`}>
                    {PLATFORMS.map((p) => <option key={p.slug} value={p.slug}>{p.label}</option>)}
                  </select>
                </div>
                {row.platform === "other" && (
                  <div className="sm:col-span-3">
                    <span className="text-[11px] font-medium text-slate-500">Platform name</span>
                    <input className={input} value={row.custom_platform_name}
                      onChange={(e) => update(i, { custom_platform_name: e.target.value })}
                      placeholder="e.g. Nextdoor" data-testid={`social-custom-${i}`} />
                  </div>
                )}
                <div className={row.platform === "other" ? "sm:col-span-3" : "sm:col-span-4"}>
                  <span className="text-[11px] font-medium text-slate-500">Handle or URL</span>
                  <input className={input} value={row.handle}
                    onChange={(e) => update(i, { handle: e.target.value, url: e.target.value.startsWith("http") ? e.target.value : row.url })}
                    placeholder="@yourbusiness or full link" data-testid={`social-handle-${i}`} />
                </div>
                <div className={row.platform === "other" ? "sm:col-span-3" : "sm:col-span-5"}>
                  <span className="text-[11px] font-medium text-slate-500">Status</span>
                  <select className={sel} value={row.verification_status}
                    onChange={(e) => update(i, { verification_status: e.target.value })}
                    data-testid={`social-verify-${i}`}>
                    {VERIFY.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-2.5 flex items-center justify-between">
                <button type="button" onClick={() => update(i, { _open: !row._open })}
                  className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                  data-testid={`social-metrics-toggle-${i}`}>
                  {row._open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  Add known metrics (optional)
                </button>
                {rows.length > 1 && (
                  <button type="button" onClick={() => remove(i)} data-testid={`social-remove-${i}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>

              {row._open && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="text-[11px] text-slate-500 mb-2">Owner-provided metrics are labelled as such and only improve score confidence. Leave blank if unknown.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    <MetricNum i={i} k="followers" label="Followers" ph="1500" />
                    <MetricNum i={i} k="posts_last_30d" label="Posts / 30 days" ph="8" />
                    <MetricNum i={i} k="avg_engagement_rate" label="Engagement %" ph="3.5" />
                    <MetricNum i={i} k="local_audience_pct" label="Local audience %" ph="60" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-2.5">
                    <MetricTri i={i} k="profile_complete" label="Profile complete?" />
                    <MetricTri i={i} k="link_in_bio" label="Link in bio?" />
                    <MetricTri i={i} k="booking_or_order_link" label="Booking link?" />
                    <MetricTri i={i} k="responds_to_comments" label="Responds to comments?" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-2.5">
                    <MetricNum i={i} k="profile_visits_30d" label="Profile visits / 30d" ph="" />
                    <MetricNum i={i} k="link_clicks_30d" label="Link clicks / 30d" ph="" />
                    <MetricNum i={i} k="leads_30d" label="Leads / 30d" ph="" />
                    <MetricNum i={i} k="bookings_30d" label="Bookings / 30d" ph="" />
                  </div>
                </div>
              )}
            </div>
          ))}

          <button type="button" onClick={add} data-testid="social-add-account"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700">
            <Plus className="w-4 h-4" /> Add another account
          </button>

          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
            We never ask for your social media passwords. We only review public profiles and the metrics you choose to share.
          </div>
        </div>
      )}

      {presence === "no" && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-2 text-[12px] text-indigo-800" data-testid="social-none-note">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          No problem — your report will include a recommendation (and optional setup service) to get you started on the right platforms.
        </div>
      )}
    </div>
  );
}

export default SocialAccountsInput;
